import { Box, styled, Button } from '@mui/material'
import makeStyles from '@mui/styles/makeStyles'
import {
    useAllApplications,
    useCheckAccessToCheckInOperation,
    useComputerCheckIn,
    useCurrentPerson,
} from 'hooks'
import moment from 'moment'
import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ComputerSessionRequestDetails from '../SessionRequestDetail'
import appConfig from 'config'
import MasterLockOverlay from 'packages/eid-ui/MasterLockOverlay'
import { useAppState } from 'appContext'
import { PasswordModal } from 'components/PasswordModal'
import { Avatar } from 'packages/eid-ui'
import { getFullImageUrl } from 'utils'
import ComputerConnectBtn from 'components/DynamicComponents/ComputerConnectButton'
import ComputersCheckInButton from 'components/DynamicComponents/ComputersCheckInButton'
import { useIsSmallScreen, useUrlGenerator } from 'packages/core'
import classNames from 'classnames'
import { textTransform } from '@mui/system'
const smallScreenWidth = '1022px'

interface PaddedDivProps {
    isSmallScreen: boolean
}

export const PaddedDiv = styled(Box)<PaddedDivProps>(({ isSmallScreen }) => ({
    padding: isSmallScreen ? '0 16px' : '0 31px',
}))

const useStyles = makeStyles((theme) => ({
    requestmaincontainer: {
        borderRadius: '0.8rem',
        boxShadow: '0 0.4rem 1.6rem 0 rgba(0, 0, 0, 0.05)',
        border: 'solid 0.1rem rgba(0, 0, 0, 0.11)',
        background: theme?.palette?.background?.paper,
        marginTop: '1.7rem',
        paddingBottom: '2rem',
        overflow: 'hidden',
    },

    requestHeadertitle: {
        fontSize: '1.4rem',
        fontWeight: 600,
        marginTop: '0.5rem',
        marginBottom: '0.5rem',
    },
    requestHeader: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        borderBottom: 'solid 0.1rem rgba(0, 0, 0, 0.11)',
        padding: '0.4rem 2rem',
        backgroundImage:
            'linear-gradient(0deg, rgb(0, 0, 0, 0.02) 12.5%, rgb(255, 255, 255) 12.5%, rgb(255, 255, 255) 50%, rgb(0, 0, 0, 0.02) 50%, rgb(0, 0, 0, 0.02) 62.5%, rgb(255, 255, 255) 62.5%, rgb(255, 255, 255) 100%)',
        backgroundSize: '0.8rem 0.8rem',
    },
    requestHeadertitlebutton: {
        '& button': {
            fontSize: '1.1rem',
            color: theme?.palette?.common?.white,
            background: theme?.palette?.secondary?.main,
            borderRadius: '0.5rem',
            padding: '0.1rem 0.4rem 0.1rem',
            textTransform: 'capitalize',
            '&:hover': {
                background: theme?.palette?.secondary?.main,
            },
        },
    },
    requestitemshow: {
        display: 'flex',
        justifyContent: 'space-between',
        padding: '0.5rem 2rem',
        marginTop: '0.7rem',
        [`@media (max-width:${smallScreenWidth})`]: {
            flexDirection: 'column',
            alignItems: 'start',
            gap: '1.6rem',
        },
    },
    requestleftshow: {
        // TODO: Commenting until we get new design
        // width: '25%',
    },
    requestitemtitle: {
        fontSize: '1.2rem',
        fontWeight: 'bold',
        color: '#9b9b9b',
        margin: '0rem',
        textTransform: 'uppercase',
        [`@media (max-width:${smallScreenWidth})`]: {
            fontWeight: '600',
        },
    },
    requestitemheading: {
        fontSize: '1.4rem',
        fontWeight: 500,
        margin: '0rem',
    },
    requestitemheadingLink: {
        '& a': {
            color: `${theme?.palette?.primary?.main} !important`,
            textDecoration: 'none !important',
        },
    },
    requestitemtime: {
        fontSize: '1.4rem',
        fontWeight: 500,
        color: theme?.palette?.common?.black,
        margin: '0rem',
        '& button': {
            fontSize: '1.1rem',
            borderRadius: '0.5rem',
            padding: '0.1rem 0.4rem 0.1rem',
            textTransform: 'capitalize',
            '&:hover': {
                background: theme?.palette?.secondary?.main,
            },
        },
    },
    checkinbutton: {
        fontSize: '1.1rem',
        color: theme?.palette?.common?.white,
        background: theme?.palette?.primary?.main,
        borderRadius: '0.5rem',
        '&:hover': {
            background: theme?.palette?.primary?.main,
        },
    },
    avtarsection: {
        display: 'flex',
        alignItems: 'center',
        ' & > div': {
            height: '2rem',
            width: '2rem',
        },
    },
    avtarname: {
        marginLeft: '0.5rem',
    },
    requestconnectbtn: {
        ' & button': {
            padding: '0',
            ' & div': {
                maxWidth: 'auto !important',
            },
        },
    },
}))

export interface ComputerSessionRequestTileProps {
    item: any
    parent: any
}

export const ComputerSessionRequestTile: FC<ComputerSessionRequestTileProps> = (
    props,
) => {
    const { item, parent } = props
    item.checkOutId = item.id
    const [{ masterPassword }]: any = useAppState()
    const [open, setOpen] = useState('')
    const classes = useStyles()
    const { t } = useTranslation()
    const [showDetailsDialog, setShowDetalsDialog] = useState(false)
    const { getMyTasksUrl } = useUrlGenerator(useAllApplications)
    const isSmallScreen = useIsSmallScreen()

    const { data: currentPerson } = useCurrentPerson()
    const handleDetailClick = () => {
        setShowDetalsDialog(true)
    }
    const isRequestedBySelf = item.isSelfRequested

    const { data: hasAccessToCheckIn } = useCheckAccessToCheckInOperation(
        item.externalCredentialID,
        isRequestedBySelf === false && item.showCheckIn,
    )
    const {
        mutateAsync: checkInComputer,
        data: computerChkIn,
        isError,
    } = useComputerCheckIn()

    const handleCheckIn = () => {
        checkInComputer(parent.checkOutId)
    }

    useEffect(() => {
        if (computerChkIn && !isError) {
            setShowDetalsDialog(false)
        }
    }, [computerChkIn, isError])

    const handleConnect = () => {
        const wfParams = []
        wfParams.push(`TargetPersonGuid=${currentPerson.id}`)
        wfParams.push(`AccessRequestPolicyID=${item.requestPolicyId}`)
        wfParams.push(`TargetComputerGuid=${item.id}`)
        wfParams.push(`IsAdvanceMode=${false}`)
        wfParams.push(`CloseWindowAfter=0`)
        const fullWorkflowUrl = constructApprovalWorkflowUrl(
            appConfig.BASE_EID_URL,
            'OpenPSMSessionWF',
            wfParams,
        )
        window.open(fullWorkflowUrl, '_blank', 'noopener,noreferrer')
    }

    const handleMasterUnlock = () => {
        setOpen('enterPassword')
    }
    return (
        <>
            {!masterPassword && (
                <PasswordModal
                    showDefaultContent={false}
                    open={open}
                    setOpen={setOpen}
                />
            )}
            <PaddedDiv isSmallScreen={isSmallScreen}>
                <Box
                    sx={{
                        borderImageSource:
                            'linear-gradient(to right, rgba(1, 174, 143, 0), #04ab936e 7%, #2c83bd47 91%, rgba(48, 127, 193, 0))',
                        borderImageSlice: '1',
                        borderBottomWidth: '1px',
                        borderStyle: 'solid',
                        borderTopWidth: '0',
                        overflow: 'hidden',
                    }}
                >
                    <Box className={classes.requestmaincontainer}>
                        <Box className={classes.requestHeader}>
                            <Box
                                component="h2"
                                className={classes.requestHeadertitle}
                            >
                                {item.credentialFriendlyName
                                    ? item.credentialFriendlyName
                                    : '---'}
                            </Box>
                            <Box className={classes.requestHeadertitlebutton}>
                                <Button onClick={() => handleDetailClick()}>
                                    {t('ManageAccess_Details')}
                                </Button>
                            </Box>
                        </Box>
                        <Box className={classes.requestitemshow}>
                            <Box className={classes.requestleftshow}>
                                <Box
                                    component="h2"
                                    className={classes.requestitemtitle}
                                >
                                    {t('Common_BusinessRequest')}
                                </Box>
                                <Box
                                    component="h2"
                                    className={classNames(
                                        classes.requestitemheading,
                                        {
                                            [classes.requestitemheadingLink]:
                                                item?.businessRequestId &&
                                                item.businessRequestName,
                                        },
                                    )}
                                >
                                    {item?.businessRequestId &&
                                    item.businessRequestName ? (
                                        <a
                                            rel="noreferrer"
                                            target="_blank"
                                            href={getMyTasksUrl(
                                                `/myRequests/businessRequests?id=${item.businessRequestId}`,
                                            )}
                                        >
                                            {item.businessRequestName
                                                ? item.businessRequestName
                                                : '---'}
                                        </a>
                                    ) : item.businessRequestName ? (
                                        item.businessRequestName
                                    ) : (
                                        '---'
                                    )}
                                </Box>
                            </Box>
                            <Box className={classes.requestleftshow}>
                                <Box
                                    component="h2"
                                    className={classes.requestitemtitle}
                                >
                                    {t('Common_BusinessRequestItem')}
                                </Box>
                                <Box
                                    component="h2"
                                    className={classNames(
                                        classes.requestitemheading,
                                        {
                                            [classes.requestitemheadingLink]:
                                                item?.businessRequestId &&
                                                item.businessRequestItemName,
                                        },
                                    )}
                                >
                                    {item?.businessRequestItemId &&
                                    item.businessRequestItemName ? (
                                        <a
                                            rel="noreferrer"
                                            target="_blank"
                                            href={getMyTasksUrl(
                                                `/myRequests/businessRequestItems?id=${item.businessRequestItemId}`,
                                            )}
                                        >
                                            {item.businessRequestItemName
                                                ? item.businessRequestItemName
                                                : '---'}
                                        </a>
                                    ) : item.businessRequestItemName ? (
                                        item.businessRequestItemName
                                    ) : (
                                        '---'
                                    )}
                                </Box>
                            </Box>
                            <Box className={classes.requestleftshow}>
                                <Box
                                    component="h2"
                                    className={classes.requestitemtitle}
                                >
                                    {t('CheckedOutBy')}
                                </Box>
                                <Box className={classes.avtarsection}>
                                    <Avatar
                                        size="small"
                                        src={getFullImageUrl(
                                            item.requestedByPersonImageThumbUrl,
                                        )}
                                    />
                                    <span className={classes.avtarname}>
                                        {item.requestorFriendlyName
                                            ? item.requestorFriendlyName
                                            : '---'}
                                    </span>
                                </Box>
                            </Box>
                        </Box>

                        <Box className={classes.requestitemshow}>
                            <Box className={classes.requestleftshow}>
                                <Box
                                    component="h2"
                                    className={classes.requestitemtitle}
                                >
                                    {t('Common_StartTime')}
                                </Box>
                                <Box
                                    component="h2"
                                    className={classes.requestitemtime}
                                >
                                    {item.startTimeUtc
                                        ? moment
                                              .utc(item.startTimeUtc)
                                              .local()
                                              .format('L LT')
                                        : '---'}
                                </Box>
                            </Box>
                            <Box className={classes.requestleftshow}>
                                <Box
                                    component="h2"
                                    className={classes.requestitemtitle}
                                >
                                    {t('Common_EndTime')}
                                </Box>
                                <Box
                                    component="h2"
                                    className={classes.requestitemtime}
                                >
                                    {item.endTimeUtc
                                        ? moment
                                              .utc(item.endTimeUtc)
                                              .local()
                                              .format('L LT')
                                        : '---'}
                                </Box>
                            </Box>
                            {item.showFutureCheckOutTime ? (
                                <Box>
                                    <Box
                                        component="h2"
                                        className={classes.requestitemtitle}
                                    >
                                        {t('Common_ActualCheckOutTime')}
                                    </Box>
                                    <Box
                                        component="h2"
                                        className={classes.requestitemtime}
                                    >
                                        {t('Common_WillBeAvailableForCheckOut')}
                                        {item.userCheckedOutTimeUtc
                                            ? moment
                                                  .utc(
                                                      item.userCheckedOutTimeUtc,
                                                  )
                                                  .local()
                                                  .format('L LT')
                                            : '---'}
                                    </Box>
                                </Box>
                            ) : (
                                <Box>
                                    <Box
                                        component="h2"
                                        className={classes.requestitemtitle}
                                    >
                                        {t('Common_ActualCheckOutTime')}
                                    </Box>
                                    <Box
                                        component="h2"
                                        className={classes.requestitemtime}
                                    >
                                        {item.userCheckedOutTimeUtc
                                            ? moment
                                                  .utc(
                                                      item.userCheckedOutTimeUtc,
                                                  )
                                                  .local()
                                                  .format('L LT')
                                            : '---'}
                                    </Box>
                                </Box>
                            )}
                            {item.showActualCheckInTime && (
                                <Box className={classes.requestleftshow}>
                                    <Box
                                        component="h2"
                                        className={classes.requestitemtitle}
                                    >
                                        {t('Common_ActualCheckInTime')}
                                    </Box>
                                    <Box
                                        component="h2"
                                        className={classes.requestitemtime}
                                    >
                                        {item.userCheckedInTimeUtc
                                            ? moment
                                                  .utc(
                                                      item.userCheckedInTimeUtc,
                                                  )
                                                  .local()
                                                  .format('L LT')
                                            : '---'}
                                    </Box>
                                </Box>
                            )}
                            {item.showStatus && (
                                <Box className={classes.requestleftshow}>
                                    <Box
                                        component="h2"
                                        className={classes.requestitemtitle}
                                    >
                                        {t('Status')}
                                    </Box>
                                    <Box
                                        component="h2"
                                        className={classes.requestitemtime}
                                    >
                                        <MasterLockOverlay
                                            btnLabel={t('Common_Unlock')}
                                            showMasterLock={!masterPassword}
                                            handleMasterUnlock={
                                                handleMasterUnlock
                                            }
                                        >
                                            <Box
                                                className={
                                                    classes.requestconnectbtn
                                                }
                                            >
                                                <ComputerConnectBtn
                                                    parentStyle=""
                                                    showPrefixIcon={false}
                                                    data={item}
                                                    drawerView={false}
                                                    showCalenderIcon={false}
                                                />
                                            </Box>
                                        </MasterLockOverlay>
                                    </Box>
                                </Box>
                            )}
                            {item.showCheckIn &&
                                hasAccessToCheckIn?.hasAccess && (
                                    <Box className={classes.requestleftshow}>
                                        <Box
                                            component="h2"
                                            className={classes.requestitemtitle}
                                        >
                                            {t('Status')}
                                        </Box>
                                        <Box
                                            component="h2"
                                            className={classes.requestitemtime}
                                        >
                                            <MasterLockOverlay
                                                btnLabel={t('Common_Unlock')}
                                                showMasterLock={!masterPassword}
                                                handleMasterUnlock={
                                                    handleMasterUnlock
                                                }
                                            >
                                                <Box
                                                    className={
                                                        classes.requestconnectbtn
                                                    }
                                                >
                                                    <ComputersCheckInButton
                                                        data={{
                                                            resource: item,
                                                        }}
                                                    />
                                                </Box>
                                            </MasterLockOverlay>
                                        </Box>
                                    </Box>
                                )}
                        </Box>
                    </Box>
                </Box>
            </PaddedDiv>
            <ComputerSessionRequestDetails
                parent={parent}
                masterPassword={masterPassword}
                handleMasterUnlock={handleMasterUnlock}
                handleConnect={() => handleConnect()}
                handleCheckIn={handleCheckIn}
                canCheckIn={item.canCheckIn && hasAccessToCheckIn?.hasAccess}
                showStatus={item.showStatus}
                item={item}
                open={showDetailsDialog}
                onClose={() => setShowDetalsDialog(false)}
                title={
                    item.credentialFriendlyName
                        ? `${item.credentialFriendlyName} ${
                              item.userCheckedInTimeUtc
                                  ? t('Common_StartingAt') +
                                    ' ' +
                                    moment
                                        .utc(item.userCheckedInTimeUtc)
                                        .local()
                                        .format('L LT')
                                  : ''
                          }`
                        : '---'
                }
            />
        </>
    )
}

const constructApprovalWorkflowUrl = (
    baseEidUrl: string,
    workflowName: string,
    workflowParams: string[],
) => {
    let queryParams = ['popupNav=true']

    if (workflowParams) {
        queryParams = queryParams.concat(workflowParams)
    }

    const queryString = queryParams.join('&')
    return `${baseEidUrl}/ui?hideHeader=true&wfMsContext=true&wfMsUrl=${window.location.origin}#w/${workflowName}?${queryString}`
}

export default ComputerSessionRequestTile
